import { useState, type ReactElement } from 'react';
import { ChevronDown, RefreshCw, Sparkles } from 'lucide-react';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'src/components/ui/collapsible';
import { Skeleton } from 'src/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import {
  hasQuestionSummaryContent,
  questionSummarySections,
  shouldCollapseQuestionSummary,
} from './question-summary.helpers';

interface QuestionSummaryProps {
  question: CombinedQuestionStats;
  isGenerating?: boolean;
  hasError?: boolean;
  canRegenerate?: boolean;
  onRegenerate?: () => void;
}

function SummaryBullets({ summaries }: { summaries: string[] }): ReactElement {
  return (
    <ul className={cn(portalDs.bodySm, 'space-y-2 text-[#0D0F1A]')}>
      {summaries.map((summary, index) => (
        <li key={`${summary}-${index}`}>
          {summary}
        </li>
      ))}
    </ul>
  );
}

function QuestionSummary({
  question,
  isGenerating = false,
  hasError = false,
  canRegenerate = false,
  onRegenerate,
}: QuestionSummaryProps): ReactElement | null {
  const hasContent = hasQuestionSummaryContent(question);
  const sections = questionSummarySections(question);
  const isCollapsible = shouldCollapseQuestionSummary(question);
  const [isOpen, setIsOpen] = useState(!isCollapsible);

  if (!hasContent && !isGenerating && !hasError) {
    return null;
  }

  const body = (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.label}>
          {sections.length > 1 ? (
            <h3 className={cn(portalDs.sectionLabel, 'mb-2')}>
              {section.label}
            </h3>
          ) : null}
          <SummaryBullets summaries={section.summaries} />
        </div>
      ))}
    </div>
  );

  return (
    <div
      className="mb-5 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-4"
      data-testid="question-summary"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-white text-[#2563EB] shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
            <Sparkles className="size-3.5" />
          </span>
          <p className={portalDs.headingH3}>
            AI summary
          </p>
        </div>
        {canRegenerate && onRegenerate ? (
          <PortalDsButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRegenerate}
            disabled={isGenerating}
            data-testid="question-summary-regenerate"
          >
            <RefreshCw className={`size-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
            Regenerate
          </PortalDsButton>
        ) : null}
      </div>

      {isGenerating ? (
        <div
          className="space-y-2"
          data-testid="question-summary-generating"
        >
          <Skeleton className="h-4 w-full rounded bg-white/80" />
          <Skeleton className="h-4 w-5/6 rounded bg-white/80" />
          <Skeleton className="h-4 w-2/3 rounded bg-white/80" />
          <p className={portalDs.caption}>
            Generating summary…
          </p>
        </div>
      ) : null}

      {hasError && !isGenerating ? (
        <p
          className="text-[13px] text-[#DC2626]"
          data-testid="question-summary-error"
        >
          Could not load the AI summary. Try regenerating.
        </p>
      ) : null}

      {!isGenerating && hasContent ? (
        isCollapsible ? (
          <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={cn(portalDs.link, 'mb-2 flex w-full items-center justify-between gap-2 text-left')}
                data-testid="question-summary-toggle"
              >
                <span>
                  {isOpen ? 'Hide summary' : 'Show summary'}
                </span>
                <ChevronDown className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {body}
            </CollapsibleContent>
            {!isOpen ? (
              <p className={cn(portalDs.bodySm, 'line-clamp-2 text-[#0D0F1A]')}>
                {sections.flatMap((section) => section.summaries).join(' ')}
              </p>
            ) : null}
          </Collapsible>
        ) : (
          body
        )
      ) : null}
    </div>
  );
}

export default QuestionSummary;
